import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patchState } from '../../nested-value-getter';
import { OffSubscriptionWithCatch, TypesaurusCoreDoc } from '@res/ngg-core';
import { MachineRef, ServicingModel } from '../../schema/schema.interface';
import { ServicingFirestoreService } from '../../services/servicing.firestore.service';
import { ServicingActions } from './servicing.action';
import { ServicingStateModel } from './servicing.store';

/**
 * Firestore's ceiling on the number of comparison values in an `in` filter. Exceeding it is a
 * rejected query, not a truncated one.
 */
const FIRESTORE_IN_LIMIT = 30;

/** Splits a list into consecutive runs of at most `size`; an empty input yields no chunks. */
function chunked<Item>(items: Item[], size: number): Item[][] {
  const chunks: Item[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

@State<ServicingStateModel>({
  name: 'servicing',
  defaults: {
    servicing: [],
    isSubscribed: false,
    isLoading: false,
    filter: undefined,
  },
})
@Injectable()
export class ServicingState {
  private servicingService = inject(ServicingFirestoreService);

  @Action(ServicingActions.GetAllServicing)
  async getAllServicing(ctx: StateContext<ServicingStateModel>) {
    ctx.patchState({ isLoading: true });
    const servicing = await this.servicingService.GetAll();
    ctx.patchState({ servicing: servicing.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }
  /**
   * Scoped to the caller's own machines when refs are supplied, unscoped for an admin.
   *
   * The unscoped listener is not merely over-broad for a partner, it is rejected: Firestore
   * evaluates `/servicing`'s list rule against every document a query returns and fails the whole
   * query if any one of them fails, so the Service page stayed empty for every partner. Because
   * `isSubscribed` latched before the subscription was known to work, and nothing cleared it, the
   * page could not recover either. It is cleared on failure now.
   */
  @Action(ServicingActions.InitServicing)
  initServicing(ctx: StateContext<ServicingStateModel>, { machineRefs }: ServicingActions.InitServicing) {
    const state = ctx.getState();
    if (state.isSubscribed) {
      return;
    }
    ctx.patchState({ isSubscribed: true });

    if (!machineRefs) {
      const off = this.servicingService.OnGetAll((data) =>
        ctx.dispatch(new ServicingActions.UpdateServicingResult(data)),
      );
      this.watchSubscription(ctx, off);
      ctx.patchState({ off });
      return;
    }

    // One listener per chunk, because Firestore rejects an `in` filter carrying more than
    // FIRESTORE_IN_LIMIT values outright. A partner with more machines than that got no servicing
    // at all, which is the same empty page the scoping was meant to fix.
    const chunks = chunked(machineRefs, FIRESTORE_IN_LIMIT);
    const resultsByChunk: Array<Array<TypesaurusCoreDoc<ServicingModel> | null | undefined>> = chunks.map(() => []);
    const offHandles = chunks.map((chunk, chunkIndex) => {
      const off = this.servicingService.OnQueryAll(
        this.servicingService.SetQuery({ filterModel: { belongsTo: { filterType: 'set', values: chunk } } }).runs,
        (data) => {
          // Merged rather than dispatched straight through: each listener only ever reports its own
          // chunk, so publishing one would replace the other chunks' rows with nothing.
          resultsByChunk[chunkIndex] = data;
          ctx.dispatch(new ServicingActions.UpdateServicingResult(resultsByChunk.flat()));
        },
      );
      this.watchSubscription(ctx, off);
      return off;
    });

    // A single handle that closes every listener, so CloseServicingConnection stays one call and
    // cannot leave a chunk subscribed.
    const off = (() => offHandles.forEach((offHandle) => offHandle())) as OffSubscriptionWithCatch;
    off.catch = (onError) => {
      offHandles.forEach((offHandle) => offHandle.catch(onError));
      return off;
    };
    ctx.patchState({ off });
  }

  @Action(ServicingActions.CloseServicingConnection)
  closeServicingConnection(ctx: StateContext<ServicingStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed && state.off) {
      state.off();
    }
    ctx.patchState({ isSubscribed: false });
  }

  @Action(ServicingActions.UpdateServicing)
  async updateMachine(
    ctx: StateContext<ServicingStateModel>,
    { servicingData, ref }: ServicingActions.UpdateServicing,
  ) {
    await this.servicingService.Update(ref, servicingData);
  }

  /**
   * A subscription that never attached must not leave `isSubscribed` latched, or the page stays
   * empty for the rest of the session with no way to retry.
   */
  private watchSubscription(ctx: StateContext<ServicingStateModel>, off: OffSubscriptionWithCatch) {
    off.catch((err) => {
      ctx.patchState({ isSubscribed: false });
      ctx.dispatch(new ServicingActions.ServicingError(err as Error));
    });
  }

  @Action(ServicingActions.UpdateServicingResult)
  updateServicingResult(ctx: StateContext<ServicingStateModel>, { data }: ServicingActions.UpdateServicingResult) {
    patchState<ServicingStateModel>(ctx, 'servicing', data);
  }

  @Action(ServicingActions.QueryServicing)
  async queryServicing(ctx: StateContext<ServicingStateModel>, { filter }: ServicingActions.QueryServicing) {
    ctx.patchState({ isLoading: true });
    const servicing = await this.servicingService.QueryAll(filter.query);
    ctx.patchState({ servicing: servicing.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }

  @Action(ServicingActions.SetSelectedMachine)
  setSelectedMachine(ctx: StateContext<ServicingStateModel>, { selectedMachine }: ServicingActions.SetSelectedMachine) {
    ctx.patchState({ selectedMachine });
  }
  @Action(ServicingActions.ServicingError)
  machineError(ctx: StateContext<ServicingStateModel>, { error }: ServicingActions.ServicingError) {
    console.error({ error });
    // await this.servicingService.Update(id, machineData);
  }
  @Selector()
  static servicing(state: ServicingStateModel) {
    return state.servicing;
  }

  @Selector()
  static servicingByMachines(state: ServicingStateModel) {
    const machineRef: MachineRef | undefined = state?.selectedMachine?.ref;
    if (machineRef) {
      return state.servicing.filter(
        (machine) =>
          machine.belongsTo?.id === machineRef?.id &&
          machine.belongsTo?.collection?.path === machineRef?.collection?.path,
      );
    }
    return undefined;
  }

  @Selector()
  static servicingState(state: ServicingStateModel) {
    return state;
  }
}
